import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";

/**
 * A text field with field-wise templates:
 *  - type-ahead suggestions (from templates saved for this fieldKey)
 *  - "+" saves current text as a new template
 *  - "⚙" opens a manager to edit / delete templates for this field
 *
 * Props: fieldKey, label, value, onChange, rows
 */
export default function TemplateField({
  fieldKey,
  label,
  value,
  onChange,
  rows = 2,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [mode, setMode] = useState(null); // null | 'save' | 'manage'
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Close any popover on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setMode(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggestions = (q) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const list = await api.suggestTemplates(fieldKey, q);
        setSuggestions(list);
        setActive(0);
        setOpen(true);
      } catch {
        setSuggestions([]);
      }
    }, 180);
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    // query on the last line being typed so suggestions stay relevant mid-note
    const lastLine = e.target.value.split("\n").pop();
    fetchSuggestions(lastLine);
  };

  const applyTemplate = (t) => {
    const trimmed = (value || "").trimEnd();
    onChange(trimmed ? trimmed + "\n" + t.content : t.content);
    setOpen(false);
    api.markTemplateUsed(t.id).catch(() => {});
  };

  const handleKey = (e) => {
    if (!open || suggestions?.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % suggestions?.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a - 1 + suggestions?.length) % suggestions?.length);
    } else if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      applyTemplate(suggestions[active]);
    } else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="tf" ref={wrapRef}>
      <div className="tf-head">
        <label>{label}</label>
        <div className="tf-tools">
          <button
            type="button"
            title="Show all templates"
            onClick={() => fetchSuggestions("")}
          >
            ▾
          </button>
          <button
            type="button"
            title="Save current text as template"
            onClick={() => {
              setOpen(false);
              setMode(mode === "save" ? null : "save");
            }}
          >
            +
          </button>
          <button
            type="button"
            title="Edit templates"
            onClick={() => {
              setOpen(false);
              setMode(mode === "manage" ? null : "manage");
            }}
          >
            ⚙
          </button>
        </div>
      </div>
      <textarea
        rows={rows}
        value={value || ""}
        onChange={handleChange}
        onKeyDown={handleKey}
        onFocus={() => fetchSuggestions((value || "").split("\n").pop())}
        placeholder="Type to get suggestions… (Ctrl+Enter applies highlighted)"
      />

      {open && suggestions?.length > 0 && (
        <ul className="tf-suggest">
          {suggestions?.map((s, i) => (
            <li
              key={s.id}
              className={i === active ? "active" : ""}
              onMouseDown={(e) => {
                e.preventDefault();
                applyTemplate(s);
              }}
            >
              <b>{s.title}</b>
              <span>{s.content}</span>
            </li>
          ))}
        </ul>
      )}

      {mode === "save" && (
        <SaveTemplate
          fieldKey={fieldKey}
          initial={value}
          onDone={() => setMode(null)}
        />
      )}
      {mode === "manage" && (
        <ManageTemplates
          fieldKey={fieldKey}
          onClose={() => setMode(null)}
          onApply={applyTemplate}
        />
      )}
    </div>
  );
}

function SaveTemplate({ fieldKey, initial, onDone }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(initial || "");
  const [err, setErr] = useState("");
  const save = async () => {
    if (!title.trim() || !content.trim()) {
      setErr("Title and content required");
      return;
    }
    try {
      await api.createTemplate({ fieldKey, title: title.trim(), content });
      onDone();
    } catch (e) {
      setErr(e.message);
    }
  };
  return (
    <div className="tf-pop">
      <div className="tf-pop-title">New template</div>
      <input
        autoFocus
        placeholder="Template name"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <textarea
        rows={3}
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />
      {err && <div className="err">{err}</div>}
      <div className="row-end">
        <button type="button" onClick={onDone}>
          Cancel
        </button>
        <button type="button" className="primary" onClick={save}>
          Save
        </button>
      </div>
    </div>
  );
}

function ManageTemplates({ fieldKey, onClose, onApply }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // {id,title,content}
  const load = () =>
    api
      .listTemplates(fieldKey)
      .then(setList)
      .catch(() => setList([]));
  useEffect(() => {
    load();
  }, [fieldKey]);

  const save = async () => {
    await api.updateTemplate(editing.id, {
      title: editing.title,
      content: editing.content,
    });
    setEditing(null);
    load();
  };
  const remove = async (id) => {
    // if (!confirm("Delete this template?")) return;
    await api.deleteTemplate(id);
    load();
  };

  return (
    <div className="tf-pop wide">
      <div className="tf-pop-title">
        Templates · {fieldKey.replace("_", " ")}{" "}
        <button type="button" onClick={onClose}>
          ✕
        </button>
      </div>
      {list?.length === 0 && (
        <div className="muted">No templates yet. Use + to add one.</div>
      )}
      <div className="tf-list">
        {list?.map((t) =>
          editing?.id === t.id ? (
            <div key={t.id} className="tf-edit">
              <input
                value={editing.title}
                onChange={(e) =>
                  setEditing({ ...editing, title: e.target.value })
                }
              />
              <textarea
                rows={3}
                value={editing.content}
                onChange={(e) =>
                  setEditing({ ...editing, content: e.target.value })
                }
              />
              <div className="row-end">
                <button type="button" onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <button type="button" className="primary" onClick={save}>
                  Update
                </button>
              </div>
            </div>
          ) : (
            <div key={t.id} className="tf-item">
              <div className="tf-item-text" title={t.content}>
                <b>{t.title}</b>
                <span>{t.content}</span>
              </div>
              <div className="tf-item-actions">
                <button type="button" title="Apply" onClick={() => onApply(t)}>
                  ↵
                </button>
                <button
                  type="button"
                  title="Edit"
                  onClick={() =>
                    setEditing({ id: t.id, title: t.title, content: t.content })
                  }
                >
                  ✎
                </button>
                <button
                  type="button"
                  title="Delete"
                  onClick={() => remove(t.id)}
                >
                  🗑
                </button>
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
}
